/**
 * WebSocket: backend o'zgarish xabarini yuboradi, biz tegishli so'rovlarni yangilaymiz.
 * Aloqa uzilsa "offline" holati ko'rsatiladi, qayta ulanganda hammasi qayta so'raladi (02-yechim, 8-band).
 */
import { useSyncExternalStore } from "react";
import { queryClient } from "./queryClient";
import { checkVersion } from "@/lib/version";

export type ConnectionState = "connecting" | "online" | "offline";

export interface EntityEvent {
  type: "event";
  entity: string;
  id: number | string;
  action: string;
  version?: number | null;
  actor_id?: number | null;
  ts: string;
  extra?: Record<string, unknown>;
}

type Listener = (event: EntityEvent) => void;

let socket: WebSocket | null = null;
let state: ConnectionState = "offline";
let attempts = 0;
let reconnectTimer: number | null = null;
let shouldRun = false;
const stateListeners = new Set<() => void>();
const eventListeners = new Set<Listener>();

function setState(next: ConnectionState) {
  state = next;
  stateListeners.forEach((l) => l());
}

export function startRealtime() {
  if (shouldRun) return;
  shouldRun = true;
  connect();
}

export function stopRealtime() {
  shouldRun = false;
  if (reconnectTimer) window.clearTimeout(reconnectTimer);
  socket?.close();
  socket = null;
  setState("offline");
}

function connect() {
  if (!shouldRun) return;
  setState("connecting");
  const proto = location.protocol === "https:" ? "wss" : "ws";
  const ws = new WebSocket(`${proto}://${location.host}/ws/events/`);
  socket = ws;
  ws.onopen = () => {
    const wasOffline = attempts > 0;
    attempts = 0;
    setState("online");
    // Uzilish paytida o'tkazib yuborilgan o'zgarishlar bo'lishi mumkin: hammasini qayta so'raymiz.
    // Uzilish ko'pincha deploy (backend qayta ko'tariladi): yangi build chiqqan bo'lsa tab o'zini yangilaydi
    if (wasOffline) {
      queryClient.invalidateQueries();
      void checkVersion();
    }
  };
  ws.onmessage = (msg) => {
    let data: EntityEvent | { type: string };
    try {
      data = JSON.parse(msg.data);
    } catch {
      return;
    }
    if (data.type === "event") {
      const ev = data as EntityEvent;
      queryClient.invalidateQueries({ queryKey: [ev.entity] });
      // Menyu belgilari (tasdiqlar, eslatmalar, o'qilmaganlar) -- shu hodisalardan keyin ham yangilansin
      if (ev.entity === "approval" || ev.entity === "reminder" || ev.entity === "notification") queryClient.invalidateQueries({ queryKey: ["counts"] });
      // Truck: carrier va vakansiyadagi bo'sh truck soni, bosh sahifadagi muddat ogohlantirishi
      if (ev.entity === "truck") ["carrier", "job", "dashboard"].forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }));
      eventListeners.forEach((l) => l(ev));
    } else if (data.type === "notification") {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
      queryClient.invalidateQueries({ queryKey: ["counts"] });
      eventListeners.forEach((l) => l({ ...(data as object), entity: "notifications", id: 0, action: "new", ts: "" } as EntityEvent));
    }
  };
  ws.onclose = (ev) => {
    socket = null;
    setState("offline");
    if (!shouldRun || ev.code === 4401) return;
    attempts += 1;
    const delay = Math.min(30_000, 1000 * 2 ** Math.min(attempts, 5));
    reconnectTimer = window.setTimeout(connect, delay);
  };
  ws.onerror = () => ws.close();
}

export function useConnectionState(): ConnectionState {
  return useSyncExternalStore(
    (cb) => {
      stateListeners.add(cb);
      return () => stateListeners.delete(cb);
    },
    () => state,
  );
}

export function subscribeEvents(listener: Listener) {
  eventListeners.add(listener);
  return () => eventListeners.delete(listener);
}
